import { ConsultancyService, StoreService } from "@3gaam/api/services";
import {
  ConsulteeApplicantsOutput,
  GoOfflineInput,
  GoOfflineOutput,
  GoOnlineInput,
  GoOnlineOutput,
} from "@3gaam/api/types/consultancy";
import { ConsulteeApplicantsInput } from "@3gaam/api/types/consultancyMapped";

import { StackHolderPaymentsOutput } from "@3gaam/api/types/store";
import { StackHolderPaymentsInput } from "@3gaam/api/types/storeMapped";
import notify from "@3gaam/shared/components/general/notification";
import { createQueryStringParameters } from "@3gaam/shared/hooks/useQuery";

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

type ConsulteeState = {
  applicant: ConsulteeApplicantsOutput | null;
  isApplicantsLoading: boolean;
  payment: StackHolderPaymentsOutput | null;
  isPaymentLoading: boolean;
  onlineStatusPopup: {
    show: boolean;
    mode: "toggle" | "Update";
    skipOfflineConfirm?: boolean;
  };
};

const initialState: ConsulteeState = {
  applicant: null,
  isApplicantsLoading: false,
  payment: null,
  isPaymentLoading: false,
  onlineStatusPopup: {
    show: false,
    mode: "toggle",
    skipOfflineConfirm:
      localStorage.getItem("skipOfflineConfirm") === "true" || undefined,
  },
};


export const consulteeApplicantAction = createAsyncThunk<ConsulteeApplicantsOutput, ConsulteeApplicantsInput>(
  "consultancyQuery/consulteeApplicants", 
  async (payload, thunkAPI) => {
    const queryString = createQueryStringParameters({ ...(payload || {}) });
    const res = await ConsultancyService.consulteeApplicants(queryString);
    return res.data;
  });

export const goOnlineAction = createAsyncThunk<GoOnlineOutput, GoOnlineInput>(
  "consultancyQuery/goOnline",
  async (payload, thunkAPI) => {
    return ConsultancyService.goOnline(payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error);
      });
  },
);

export const goOfflineAction = createAsyncThunk<
  GoOfflineOutput & { skipOfflineConfirm: boolean },
  GoOfflineInput
>("consultancyQuery/goOffline", async (payload, thunkAPI) => {
  const res = await ConsultancyService.goOffline(payload);
  return {
    ...res.data,
    skipOfflineConfirm: payload.PersistConfirmationSkipped,
  };
});

export const stackHolderPaymentsAction = createAsyncThunk<StackHolderPaymentsOutput, StackHolderPaymentsInput>(
  "StackHolderQuery/StackHolderPayments",
  async (payload, thunkAPI) => {
    const queryString = createQueryStringParameters({ ...(payload || {}) });
    const res = await StoreService.stackHolderPayments(queryString);
    return res.data;
  },
);

export const authSlice = createSlice({
  name: "consultee",
  initialState,
  reducers: {
    setShowOnlineStatusPopup: {
      reducer: (
        state,
        {
          payload: { show, mode },
        }: PayloadAction<ConsulteeState["onlineStatusPopup"]>,
      ) => {
        state.onlineStatusPopup.show = show;
        show && (state.onlineStatusPopup.mode = mode);
      },
      prepare: (show, mode = "toggle") => {
        return {
          payload: {
            show,
            mode,
          },
        };
      },
    },
    resetConsulteeApplicantItemsAction: (state) => {
      if (state.applicant?.Items) {
        state.applicant.Items = initialState.applicant?.Items;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(consulteeApplicantAction.fulfilled, (state, action) => {
        const { Items, ...rest } = action.payload;
        state.applicant = {
          ...rest,
          Items: [...(state.applicant?.Items ?? []), ...(Items ?? [])],
        };

        state.isApplicantsLoading = false;
      })
      .addCase(consulteeApplicantAction.pending, (state, action) => {
        state.isApplicantsLoading = true;
      })
      .addCase(consulteeApplicantAction.rejected, (state, action) => {
        state.isApplicantsLoading = false;
      })
      .addCase(goOnlineAction.pending, (state, action) => {
        state.onlineStatusPopup.show = false;
        state.applicant && (state.applicant.OnlineStatus = true);
      })
      .addCase(goOnlineAction.fulfilled, (state, action) => {
        state.applicant &&
          (state.applicant.OnlineMessage = action.payload.Message);
      })
      .addCase(goOnlineAction.rejected, (state, action) => {
        //@ts-ignore
        let error = action?.payload?.response?.data?.Title;
        if (error) {
          notify({
            type: "warning",
            message: (
              <div>
                <small>{error}</small>
              </div>
            ),
            duration: 4000,
          });
        }
        state.applicant && (state.applicant.OnlineStatus = false);
      })
      .addCase(goOfflineAction.pending, (state, action) => {
        state.onlineStatusPopup.show = false;
        state.applicant && (state.applicant.OnlineStatus = false);
      })
      .addCase(goOfflineAction.fulfilled, (state, action) => {
        if (action.payload.skipOfflineConfirm) {
          state.onlineStatusPopup.skipOfflineConfirm = true;
          localStorage.setItem("skipOfflineConfirm", "true");
        }

        state.applicant &&
          (state.applicant.OnlineMessage = action.payload.Message);
      })
      .addCase(stackHolderPaymentsAction.pending, (state) => {
        state.isPaymentLoading = true;
      })
      .addCase(stackHolderPaymentsAction.fulfilled, (state, action) => {
        state.isPaymentLoading = false;

        if (!state.payment) {
          state.payment = action.payload;
          return;
        }

        const { Items, ...rest } = action.payload;
        state.payment.Items?.push(...(Items || []));
        state.payment = { ...state.payment, ...rest };
      })
      .addCase(stackHolderPaymentsAction.rejected, (state) => {
        state.isPaymentLoading = false;
      });
  },
});

export const { setShowOnlineStatusPopup, resetConsulteeApplicantItemsAction } =
  authSlice.actions;

export default authSlice.reducer;
